import React, { ReactElement } from "react";
import {
    Form,
    useListSlackBotsQuery,
} from "../../../__generated__/api";
import Select, { Option, SelectInstanceHandler } from "../../select/Select";

export interface SlackBotsPickerProps<TIsMulti = false> {
    isMulti?: TIsMulti;
    minRequired?: number;
    placeholder?: string;
    channelId?: string;
    value: (TIsMulti extends true ? string[] : string) | undefined;
    onSelected: (sourceId: (TIsMulti extends true ? string[] : string)) => void;
}

const SlackBots = React.forwardRef(<TIsMulti extends boolean = false>({
    isMulti, onSelected, placeholder, minRequired, value, channelId,
}: SlackBotsPickerProps<TIsMulti>, ref: React.ForwardedRef<SelectInstanceHandler>): ReactElement => {
    const { data, loading, error } = useListSlackBotsQuery({
        variables: {
            id: channelId,
        },
    });

    if (loading || error) {
        return (
            <div>Loading...</div>
        );
    }

    type SelectedType = TIsMulti extends true ? readonly Option<Form>[] : Option<Form>;
    type ValueType = TIsMulti extends true ? Option<Form>[] : Option<Form>;

    const handleSelected = (selected: SelectedType) => {
        const values = (Array.isArray(selected)
            ? selected.map(row => row.value as string)
            : (selected as Option<Form>).value) as Parameters<typeof onSelected>[0];
        onSelected(values);
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const values = data?.bots?.map(bot => ({
        label: bot.name as string,
        value: bot.id.toString() as string,
        data: bot,
    })) ?? [];
    const valueMap = values.toIdMap("value");
    const selected = Array.isArray(value) ? value.map(row => valueMap[row]) : valueMap[value as string];

    return (
        <Select
            ref={ref}
            minRequired={minRequired}
            placeholder={placeholder}
            isMulti={isMulti}
            onChange={handleSelected}
            options={(values ?? []) as Option<Form>[]}
            value={selected as ValueType}
        />
    );
});

SlackBots.defaultProps = {
    isMulti: false,
    minRequired: undefined,
    placeholder: undefined,
    channelId: undefined,
};

export default SlackBots as <
    TMulti extends boolean = false,
> (
    props: SlackBotsPickerProps<TMulti> & { ref?: React.ForwardedRef<SelectInstanceHandler> }
) => ReturnType<typeof SlackBots>;
